import { useState } from "react";
import { Box, Button, Container, Stack, Typography, Divider, IconButton, Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DarkMode from "@mui/icons-material/DarkMode";
import LightMode from "@mui/icons-material/LightMode";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth-store";
import { useThemeStore } from "@/store/theme-store";
import { WhbLogo } from "@/components/WhbLogo";
import { WhbTokenHandoffDialog } from "@/components/WhbTokenHandoffDialog";
import { useWhbRealLoginEnabled } from "@/lib/features";
// startRealLogin (auth.ts) redirects to the WHB bridge page. The bridge
// isn't deployed yet, so the primary real-login button opens the
// WhbTokenHandoffDialog instead. Flip back to startRealLogin once the
// bridge ships — everything downstream (AuthCallbackPage, /api/session,
// proxy, logout) already expects the bridge-style hash payload and
// doesn't care how it was produced.
// import { startRealLogin } from "@/lib/auth";

// Inline provider glyphs
const MsIcon = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 20, height: 20 }} aria-hidden>
    <rect x="2"  y="2"  width="9" height="9" fill="#f25022"/>
    <rect x="13" y="2"  width="9" height="9" fill="#7fba00"/>
    <rect x="2"  y="13" width="9" height="9" fill="#00a4ef"/>
    <rect x="13" y="13" width="9" height="9" fill="#ffb900"/>
  </Box>
);
const GoogleIcon = () => (
  <Box component="svg" viewBox="0 0 48 48" sx={{ width: 20, height: 20 }} aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C33.6 6.2 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C33.6 6.2 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5 0 9.4-1.9 12.7-5l-5.9-5c-1.9 1.3-4.3 2-6.8 2-5.2 0-9.7-3.3-11.3-8l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l.1-.1 5.9 5C37 39 44 33.8 44 24c0-1.2-.1-2.3-.4-3.5z"/>
  </Box>
);
const FbIcon = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 20, height: 20 }} aria-hidden>
    <path fill="#1877F2" d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.23 2.68.23v2.95h-1.51c-1.49 0-1.95.92-1.95 1.86V12h3.32l-.53 3.47h-2.79v8.38A12 12 0 0 0 24 12z"/>
  </Box>
);

export function SignInPage() {
  const signIn = useAuthStore((s) => s.signIn);
  const navigate = useNavigate();
  const { mode, toggle } = useThemeStore();
  const realLoginEnabled = useWhbRealLoginEnabled();
  const [handoffOpen, setHandoffOpen] = useState(false);
  // Sergey 2026-04-25: split desktop / mobile sign-in UX. Desktop only
  // gets the popup-paste flow ("Log in"), mobile only gets the QR pair
  // entry ("Pair this device") — neither button works on the other
  // form factor (no DevTools on mobile / no need for QR on desktop).
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  function mockSignIn(provider: "microsoft" | "google" | "facebook") {
    signIn({
      name: "Sergey Klimuk",
      email: "klimuk@4teamcorp.com",
      provider
    });
    navigate("/accounts", { replace: true });
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 6,
        // Solid theme background — black on dark (default), white on light
        bgcolor: "background.default",
        position: "relative"
      }}
    >
      {/* Theme toggle — top right */}
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <Tooltip title={`Switch to ${mode === "dark" ? "light" : "dark"} mode`}>
          <IconButton onClick={toggle}>
            {mode === "dark" ? <LightMode /> : <DarkMode />}
          </IconButton>
        </Tooltip>
      </Box>

      <Container maxWidth="xs">
        <Stack spacing={4} alignItems="stretch">
          <Stack alignItems="center" spacing={2.5}>
            <WhbLogo variant="full" size={64} />
            <Stack alignItems="center" spacing={0.5}>
              <Typography variant="h1" sx={{ fontSize: "2rem !important" }}>Welcome Back</Typography>
              <Typography variant="body2" color="text.secondary">
                Please sign in to continue
              </Typography>
            </Stack>
          </Stack>

          <Stack spacing={1.5}>
            <Button variant="outlined" size="large" fullWidth startIcon={<MsIcon />}
                    onClick={() => mockSignIn("microsoft")}
                    sx={{ justifyContent: "flex-start", pl: 2.5 }}>
              Sign in with Microsoft
            </Button>
            <Button variant="outlined" size="large" fullWidth startIcon={<GoogleIcon />}
                    onClick={() => mockSignIn("google")}
                    sx={{ justifyContent: "flex-start", pl: 2.5 }}>
              Sign in with Google
            </Button>
            <Button variant="outlined" size="large" fullWidth startIcon={<FbIcon />}
                    onClick={() => mockSignIn("facebook")}
                    sx={{ justifyContent: "flex-start", pl: 2.5 }}>
              Sign in with Facebook
            </Button>
          </Stack>

          <Divider>
            <Typography variant="caption" color="text.secondary">
              {realLoginEnabled ? "or use your real WHB account" : "Demo mode — any provider works"}
            </Typography>
          </Divider>

          {realLoginEnabled && !isMobile && (
            // Desktop: popup + one-line console paste. Mobile cannot
            // run that flow because there are no DevTools, so this
            // entire block is hidden on small viewports — mobile users
            // see only the "Pair this device" link below.
            <Stack spacing={1}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => setHandoffOpen(true)}
                endIcon={<OpenInNew sx={{ fontSize: 18, opacity: 0.9 }} />}
                sx={{
                  justifyContent: "center",
                  gap: 1,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
                startIcon={<WhbLogo variant="mark" size={24} />}
              >
                Log in
              </Button>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                Uses your existing whitehatbots.com session
              </Typography>
            </Stack>
          )}

          {realLoginEnabled && isMobile && (
            // Mobile: QR pairing is the only viable real-login path
            // (DevTools-free). This is a primary action on mobile, not
            // a secondary "fallback" link, so render it as a contained
            // button matching the desktop's "Log in" prominence.
            <Stack spacing={1}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => navigate("/pair")}
                sx={{
                  justifyContent: "center",
                  gap: 1,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
                startIcon={<WhbLogo variant="mark" size={24} />}
              >
                Pair this device
              </Button>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                Scan a QR from your already-signed-in computer
              </Typography>
            </Stack>
          )}

          <Typography variant="caption" color="text.secondary" textAlign="center">
            By signing in, you agree to our{" "}
            <Box component="a" href="#" sx={{ color: "primary.main", textDecoration: "none" }}>Terms</Box> and{" "}
            <Box component="a" href="#" sx={{ color: "primary.main", textDecoration: "none" }}>Privacy Policy</Box>.
          </Typography>

          <Typography variant="caption" color="text.secondary" textAlign="center">
            Don&apos;t have an account?{" "}
            <Box component="a" href="#" sx={{ color: "primary.main", fontWeight: 600, textDecoration: "none" }}>
              Sign Up
            </Box>
          </Typography>
        </Stack>
      </Container>

      <WhbTokenHandoffDialog
        open={handoffOpen}
        onClose={() => setHandoffOpen(false)}
      />
    </Box>
  );
}
